import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Header from '@/components/Header';
import CalendlyWidget from '@/components/feature/CalendlyWidget';

const CALENDLY_URL = 'https://calendly.com/morpheniq-info/30min';

export default function BookMeeting() {
  const { t } = useTranslation();
  const [scheduled, setScheduled] = useState(false);

  return (
    <>
      <Header />
      <main className="min-h-screen bg-background-50">
        {/* Header spacing */}
        <div className="h-14 md:h-16 lg:h-20" />

        {/* Hero — MorphenIQ branded */}
        <section className="relative w-full bg-primary-500 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary-600/40 via-transparent to-primary-700/40" />
          <div className="relative w-full px-4 md:px-8 lg:px-12 py-12 md:py-16 lg:py-20">
            <div className="max-w-5xl mx-auto text-center">
              <span className="inline-block px-3 py-1 bg-white/10 text-white/90 text-xs font-semibold rounded-full uppercase tracking-wider mb-4 border border-white/20">
                {t('meeting.badge')}
              </span>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
                {t('meeting.title')}
              </h1>
              <p className="text-base md:text-lg text-white/80 max-w-2xl mx-auto">
                {t('meeting.subtitle')}
              </p>
            </div>
          </div>
        </section>

        {/* Main content — two columns */}
        <section className="w-full px-4 md:px-8 lg:px-12 py-8 md:py-12 lg:py-16">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12">
              {/* Left column — MorphenIQ info */}
              <div className="lg:col-span-2 space-y-6">
                {/* Meeting details card */}
                <div className="bg-white border border-background-200 rounded-xl p-5 md:p-6">
                  <h2 className="text-sm font-semibold text-foreground-950 uppercase tracking-wider mb-4">
                    {t('meeting.aboutLabel')}
                  </h2>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-6">
                    {t('meeting.aboutText')}
                  </p>

                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-background-50 rounded-lg p-4 border border-background-200">
                      <div className="flex items-center gap-2 text-primary-600 mb-1">
                        <i className="ri-time-line text-lg" />
                        <span className="text-xs font-semibold uppercase tracking-wider">{t('meeting.durationLabel')}</span>
                      </div>
                      <p className="text-sm font-semibold text-foreground-950">{t('meeting.duration')}</p>
                    </div>
                    <div className="bg-background-50 rounded-lg p-4 border border-background-200">
                      <div className="flex items-center gap-2 text-accent-600 mb-1">
                        <i className="ri-video-chat-line text-lg" />
                        <span className="text-xs font-semibold uppercase tracking-wider">{t('meeting.typeLabel')}</span>
                      </div>
                      <p className="text-sm font-semibold text-foreground-950">{t('meeting.typeValue')}</p>
                    </div>
                  </div>
                </div>

                {/* Preparation card */}
                <div className="bg-background-50 border border-background-200 rounded-xl p-5 md:p-6">
                  <h2 className="text-sm font-semibold text-foreground-950 uppercase tracking-wider mb-4">
                    {t('meeting.prepLabel')}
                  </h2>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-secondary-500 flex items-center justify-center shrink-0 mt-0.5">
                        <i className="ri-check-line text-xs text-foreground-950" />
                      </div>
                      <span className="text-sm text-foreground-700">{t('meeting.prep1')}</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-secondary-500 flex items-center justify-center shrink-0 mt-0.5">
                        <i className="ri-check-line text-xs text-foreground-950" />
                      </div>
                      <span className="text-sm text-foreground-700">{t('meeting.prep2')}</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-secondary-500 flex items-center justify-center shrink-0 mt-0.5">
                        <i className="ri-check-line text-xs text-foreground-950" />
                      </div>
                      <span className="text-sm text-foreground-700">{t('meeting.prep3')}</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Right column — embedded Calendly widget */}
              <div className="lg:col-span-3">
                <div className="bg-white border border-background-200 rounded-xl p-5 md:p-6">
                  <div className="flex items-center gap-3 mb-5 pb-5 border-b border-background-200">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center shrink-0">
                      <i className="ri-calendar-check-line text-xl text-primary-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-950">{t('meeting.typeValue')}</p>
                      <p className="text-xs text-foreground-500">{t('meeting.duration')}</p>
                    </div>
                  </div>

                  <CalendlyWidget
                    url={CALENDLY_URL}
                    height={720}
                    onScheduled={() => setScheduled(true)}
                  />

                  {scheduled && (
                    <Link
                      to="/"
                      className="mt-0 flex items-center justify-center gap-2 w-full px-6 py-3 bg-primary-500 text-white text-sm font-semibold rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap"
                    >
                      <i className="ri-arrow-left-line" />
                      {t('meeting.backHome')}
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}